import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Edit2,
  Search,
  Loader2,
  Grid3X3,
  ArrowLeft,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Category, Subcategory } from "../types";
import { getApiUrl } from "../config/api";
import { useApi } from "../context/ApiContext";
import { useStatusUpdate } from "../shared/hooks/useStatusUpdate";
import { DeleteConfirmationModal } from "../shared/components/DeleteConfirmationModal";
import { EditStatusModal } from "../shared/components/EditStatusModal";

const SubCategoryTable: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryId } = useParams<{
    categoryId: string;
  }>();

  const { category } = location.state as { category: Category };
  const { refreshTrigger } = useApi();
  const { updateStatus, apiLoading } = useStatusUpdate(
    "/api/templates/v1.0.0/templates-subcategory"
  );

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    category
  );
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Refresh category data when refreshTrigger changes
  useEffect(() => {
    const fetchUpdatedCategory = async () => {
      try {
        const response = await fetch(getApiUrl("/api/templates/v1.0.0/stats"));
        const result = await response.json();

        if (result.status === "success" && Array.isArray(result.data)) {
          const updatedCategory = result.data.find(
            (cat: Category) => cat.categoryId === categoryId
          );
          if (updatedCategory) {
            setSelectedCategory(updatedCategory);
          }
        }
      } catch (error) {
        console.error("Error fetching updated category:", error);
      }
    };

    // Only fetch if refreshTrigger has changed (not on initial load)
    if (refreshTrigger > 0) {
      fetchUpdatedCategory();
    }
  }, [refreshTrigger, categoryId]);

  // delete modal state
  const [deleteTarget, setDeleteTarget] = useState<{
    item: Subcategory | null;
  }>({ item: null });
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // edit (state toggle) modal
  const [editTarget, setEditTarget] = useState<Subcategory | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const deleteSubcategory = (subCategoryId: string) =>
    updateStatus(subCategoryId, "Deleted");
  const toggleSubcategoryStatus = (subcategory: Subcategory) => {
    const newStatus = subcategory.status === "Active" ? "Inactive" : "Active";
    return updateStatus(subcategory.subCategoryId, newStatus);
  };

  // delete
  const handleDeleteClick = (item: Subcategory): void => {
    setDeleteTarget({ item });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteTarget.item && "subCategoryId" in deleteTarget.item) {
      const success = await deleteSubcategory(deleteTarget.item.subCategoryId);
      if (success) {
        // Remove from local state
        setSelectedCategory((prev) =>
          prev
            ? {
                ...prev,
                subcategories: prev.subcategories.filter(
                  (sub) =>
                    sub.subCategoryId !== deleteTarget.item?.subCategoryId
                ),
              }
            : prev
        );
      }
    }
    setShowDeleteModal(false);
    setDeleteTarget({ item: null });
  };

  // edit (toggle state)
  const handleEdit = (subcategory: Subcategory): void => {
    setEditTarget(subcategory);
    setShowEditModal(true);
  };

  const confirmEdit = async () => {
    if (editTarget) {
      const success = await toggleSubcategoryStatus(editTarget);
      if (success) {
        // Update local state
        setSelectedCategory((prev) =>
          prev
            ? {
                ...prev,
                subcategories: prev.subcategories.map((sub) =>
                  sub.subCategoryId === editTarget.subCategoryId
                    ? {
                        ...sub,
                        status: sub.status === "Active" ? "Inactive" : "Active",
                      }
                    : sub
                ),
              }
            : prev
        );
      }
    }
    setShowEditModal(false);
    setEditTarget(null);
  };

  const handleCreate = () => {
    navigate(`/sub-createCategory/${categoryId}`);
  };

  const handleBack = () => {
    navigate("/category");
  };

  // search filter
  const filteredSubcategories: Subcategory[] =
    selectedCategory?.subcategories.filter((sub) =>
      sub.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100">
      {/* Enhanced Header */}
      <div className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <Grid3X3 className="w-6 h-6 text-white" />
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Subcategories
                </h1>
                {selectedCategory && (
                  <p className="text-sm text-gray-500 mt-1">
                    Parent:{" "}
                    <span className="font-medium text-emerald-600">
                      {selectedCategory.categoryName}
                    </span>
                    {" • "}
                    {filteredSubcategories.length} subcategories
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleCreate}
              className="group relative bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create Subcategory</span>
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal-700 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 -z-10"></div>
            </button>
          </div>

          {/* Enhanced Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search subcategories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid gap-6 lg:gap-8">
          {filteredSubcategories.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Grid3X3 className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No subcategories found
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm
                  ? `No subcategories match "${searchTerm}"`
                  : `No subcategories in "${
                      selectedCategory?.categoryName || "this category"
                    }"`}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleCreate}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  Create First Subcategory
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredSubcategories.map((subcategory: Subcategory) => (
                <button
                  key={subcategory.subCategoryId}
                  type="button"
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/50 hover:border-emerald-300/50 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer w-full text-left"
                  onClick={() =>
                    navigate(
                      `/categories/${categoryId}/${encodeURIComponent(
                        selectedCategory?.categoryName || ""
                      )}/subcategories/${
                        subcategory.subCategoryId
                      }/${encodeURIComponent(subcategory.name)}/templates`
                    )
                  }
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      {/* Subcategory Info */}
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="relative">
                          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                            {subcategory.name.charAt(0)}
                          </div>
                          {subcategory.status === "Active" && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {subcategory.name}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2">
                            ID: {subcategory.subCategoryId.slice(-8)}
                          </p>
                          <div className="flex flex-wrap gap-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              {subcategory.templatesCount} templates
                            </span>
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                subcategory.status === "Active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {subcategory.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(subcategory);
                          }}
                          disabled={apiLoading === subcategory.subCategoryId}
                          className="p-2.5 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors group disabled:opacity-50"
                          aria-label="Edit subcategory"
                        >
                          {apiLoading === subcategory.subCategoryId ? (
                            <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          ) : (
                            <Edit2 className="w-4 h-4 text-emerald-600" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(subcategory);
                          }}
                          disabled={apiLoading === subcategory.subCategoryId}
                          className="p-2.5 bg-red-50 hover:bg-red-100 rounded-lg transition-colors group disabled:opacity-50"
                          aria-label="Delete subcategory"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal && !!deleteTarget.item}
        title="Delete Subcategory"
        itemName={deleteTarget.item?.name ?? ""}
        description="All associated templates will also be affected."
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteTarget({ item: null });
        }}
        isConfirming={
          !!deleteTarget.item &&
          "subCategoryId" in deleteTarget.item &&
          apiLoading === deleteTarget.item.subCategoryId
        }
      />

      <EditStatusModal
        isOpen={showEditModal && !!editTarget}
        itemName={editTarget?.name ?? ""}
        currentStatus={editTarget?.status ?? ""}
        newStatus={editTarget?.status === "Active" ? "Inactive" : "Active"}
        entityLabel="Update subcategory status"
        primaryColor="emerald"
        onConfirm={confirmEdit}
        onCancel={() => {
          setShowEditModal(false);
          setEditTarget(null);
        }}
        isConfirming={!!editTarget && apiLoading === editTarget.subCategoryId}
      />
    </div>
  );
};

export default SubCategoryTable;
